'use client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import type { DesktopControlClient, VncTicketResult } from 'elowen-plugin-ui-kit';
import { apiOnce, ElowenApiError, elowenClient } from '../../lib/elowenClient';
import { requestDesktopTicket } from '../../lib/desktopTicketRequest';
import { useDesktopStream } from './useDesktopStream';
import { randomId } from 'elowen-plugin-shared/randomId';

const leaseSchema = z.object({ leaseId: z.string().min(1).max(256), expiresAt: z.number().positive(), controlRevision: z.number().int().nonnegative() });
type Lease = Pick<z.infer<typeof leaseSchema>, 'leaseId' | 'controlRevision'>;
const message = (error: unknown): string => error instanceof Error ? error.message : String(error);

export function useDesktopControl(plugin: string, sessionId: string, { controlsEnabled = true, streamEnabled = true }: { controlsEnabled?: boolean; streamEnabled?: boolean } = {}): DesktopControlClient {
  const view = useMemo(() => ({ plugin, sessionId, id: randomId() }), [plugin, sessionId]);
  const viewId = view.id;
  const enabled = useRef(controlsEnabled);
  useEffect(() => { enabled.current = controlsEnabled; }, [controlsEnabled]);
  const [storedLease, setLease] = useState<{ viewId: string; value: Lease } | null>(null);
  const lease = storedLease?.viewId === viewId ? storedLease.value : null;
  const [connectedViewId, setConnectedViewId] = useState<string | null>(null);
  const viewConnected = connectedViewId === viewId;
  const setViewConnected = useCallback((connected: boolean) => setConnectedViewId(connected ? viewId : null), [viewId]);
  const [storedError, setStoredError] = useState<{ viewId: string; message: string } | null>(null);
  const error = storedError?.viewId === viewId ? storedError.message : null;
  const setError = useCallback((message: string | null) => setStoredError(message ? { viewId, message } : null), [viewId]);
  const path = useCallback((action: string) => `/plugins/${plugin}/api/desktop/${action}?sessionId=${encodeURIComponent(sessionId)}`, [plugin, sessionId]);
  const stream = useDesktopStream(sessionId ? path('stream') : undefined, streamEnabled);
  const post = useCallback(async (action: string, leaseId?: string) => elowenClient.api(path(action), {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ viewId, leaseId }),
  }), [path, viewId]);
  const takeControl = useCallback(async () => {
    if (!controlsEnabled) throw new Error('Desktop view is read-only.');
    const claimed = leaseSchema.parse(await post('takeover'));
    if (!enabled.current) { await post('release', claimed.leaseId); return; }
    setError(null);
    setLease({ viewId, value: { leaseId: claimed.leaseId, controlRevision: claimed.controlRevision } });
  }, [controlsEnabled, post, viewId, setError]);
  // A lease the server already ended (409) is the outcome a release asks for, not a failure to report.
  const release = useCallback(async (leaseId: string) => {
    try { await post('release', leaseId); }
    catch (failure) { if (!(failure instanceof Error && 'status' in failure && failure.status === 409)) throw failure; }
  }, [post]);
  const releaseControl = useCallback(async () => {
    if (!lease) return;
    await release(lease.leaseId);
    setError(null);
    setLease(null);
  }, [lease, release, setError]);
  useEffect(() => {
    if (!lease) return;
    // A suspended stream means the person no longer sees the machine and this hook no longer learns of
    // control changes, so it gives control back rather than keep a lease it cannot show truthfully.
    if (!controlsEnabled || !streamEnabled) {
      setLease(null);
      void release(lease.leaseId).catch(failure => setError(message(failure)));
      return;
    }
    if (stream.control.controlRevision > lease.controlRevision) { setLease(null); return; }
    let current = true;
    const timer = setInterval(() => {
      void post('heartbeat', lease.leaseId).catch((failure) => {
        // A heartbeat answered after this lease was released or replaced says nothing about the current one.
        if (!current) return;
        setError(message(failure));
        setLease(null);
      });
    }, 20_000);
    return () => { current = false; clearInterval(timer); };
  }, [controlsEnabled, streamEnabled, lease, post, release, stream.control.controlRevision, setError]);
  const autoClaimed = useRef<string | null>(null);
  const controlReady = stream.hasControlSnapshot && stream.connectionState?.freshness === 'fresh'
    && stream.connectionState.readiness === 'ready';
  useEffect(() => {
    const request = viewId + ':' + stream.control.controlRevision;
    // An owned lease already answered this observation, even before the claim snapshot arrives.
    // Losing it must not replay the old request and acquire control again.
    if (lease) { autoClaimed.current = request; return; }
    if (!controlReady || !controlsEnabled || !viewConnected || stream.closed || stream.control.reason !== 'requested' || autoClaimed.current === request) return;
    autoClaimed.current = request;
    void takeControl().catch((failure) => setError(message(failure)));
  }, [controlReady, controlsEnabled, viewId, viewConnected, lease, stream.closed, stream.control.reason, stream.control.controlRevision, takeControl, setError]);
  const ticket = useCallback(async (signal: AbortSignal): Promise<VncTicketResult> => {
    try {
      const result = await requestDesktopTicket(() => apiOnce(path('ticket'), {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ viewId, leaseId: lease?.leaseId }), signal,
      }));
      setError(null);
      return { kind: 'ticket', ...result };
    }
    catch (failure) {
      if (signal.aborted) throw failure;
      if (failure instanceof ElowenApiError && failure.status === 429) {
        return { kind: 'refused', reason: 'viewer_limit', retryAfterMs: failure.retryAfterMs };
      }
      setError(message(failure));
      throw failure;
    }
  }, [lease, path, viewId, setError]);
  return { lease, stream: error ? { ...stream, error } : stream, takeControl, releaseControl, ticket, setViewConnected,
    interactive: controlReady && controlsEnabled && !!lease && stream.control.state === 'user' && stream.control.controlRevision === lease.controlRevision,
    connectionKey: viewId + ':' + (lease?.leaseId ?? '') };
}
